import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart2, PieChart, TrendingUp, Clock, Users,
  Building2, Activity, Calendar, ChevronDown, RefreshCw,
  ArrowUpDown, Filter, Eye, EyeOff
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, Legend } from 'recharts';
import { useTimezone } from '../../../../../hooks/useTimezone';

const OverviewTab = ({ setError, setSuccess }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Get user's timezone (normalized abbreviation -> IANA identifier)
  const userTimezone = useTimezone();
  
  // Helper function to get display text for current date range
  const getDateRangeDisplayText = () => {
    if (dateRange === 'custom') {
      return `${customDateRange.startDate} to ${customDateRange.endDate}`;
    }
    
    const today = getDateInUserTimezone();
    switch (dateRange) {
      case 'today':
        return `Today (${today})`;
      case 'yesterday':
        return `Yesterday (${getDateInUserTimezone(-1)})`;
      case 'this_week':
        return 'This Week';
      case 'last_week':
        return 'Last Week';
      case 'this_month':
        return 'This Month';
      default:
        return dateRange;
    }
  };
  
  // Helper function to get date in user's timezone
  const getDateInUserTimezone = (offset = 0) => {
    const now = new Date();
    const userDate = new Date(now.toLocaleString("en-US", {timeZone: userTimezone}));
    userDate.setDate(userDate.getDate() + offset);
    return userDate.toISOString().split('T')[0];
  };
  
  // States
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: getDateInUserTimezone(),
    endDate: getDateInUserTimezone()
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Stats states
  const [summaryStats, setSummaryStats] = useState({
    totalHours: 0,
    activeEmployees: 0, 
    avgHoursPerEmployee: 0,
    billableRatio: 0
  });
  
  // Currently active users
  const [activeUsers, setActiveUsers] = useState({
    count: 0,
    list: []
  });
  
  // Visualization data
  const [businessUnitData, setBusinessUnitData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  
  // Chart interaction states
  const [sortBy, setSortBy] = useState('hours'); // 'hours', 'name', 'percentage'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [showOnlyTop, setShowOnlyTop] = useState(false);
  const [topCount, setTopCount] = useState(10);
  const [hiddenItems, setHiddenItems] = useState(new Set());
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Dynamic color generation
  const generateColors = (count) => {
    const baseColors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', 
      '#6366F1', '#14B8A6', '#F97316', '#84CC16', '#06B6D4', '#8B5A2B',
      '#DC2626', '#7C3AED', '#059669', '#D97706', '#0891B2', '#65A30D'
    ];
    
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    
    // Generate additional colors using HSL
    const colors = [...baseColors];
    for (let i = baseColors.length; i < count; i++) {
      const hue = (i * 360 / count) % 360;
      const saturation = 60 + (i % 3) * 15;
      const lightness = 45 + (i % 4) * 10;
      colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
    }
    
    return colors;
  };
  
  // Fetch data based on date range
  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, customDateRange]);
  
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare date parameters based on selected range
      let params = new URLSearchParams();
      
      // Always include timezone for proper server-side calculations
      params.append('timezone', userTimezone);
      
      if (dateRange === 'custom') {
        params.append('startDate', customDateRange.startDate);
        params.append('endDate', customDateRange.endDate);
      } else {
        params.append('range', dateRange);
      }
      
      // Summary stats endpoint
      const summaryResponse = await fetch(`${API_URL}/labor/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!summaryResponse.ok) {
        throw new Error('Failed to fetch summary data');
      }
      
      const summaryData = await summaryResponse.json();
        setSummaryStats({
          totalHours: safeNumber(summaryData.totalHours),
          activeEmployees: safeNumber(summaryData.activeEmployees),
          avgHoursPerEmployee: safeNumber(summaryData.avgHoursPerEmployee),
          billableRatio: safeNumber(summaryData.billableRatio)
        });
      
      // Business unit distribution endpoint
      const businessUnitsResponse = await fetch(`${API_URL}/labor/by-business-unit?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (businessUnitsResponse.ok) {
        const businessUnitResult = await businessUnitsResponse.json();
        setBusinessUnitData(businessUnitResult.distribution || []);
      }
      
      // Activity distribution endpoint
      const activitiesResponse = await fetch(`${API_URL}/labor/by-activity?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (activitiesResponse.ok) {
        const activityResult = await activitiesResponse.json();
        setActivityData(activityResult.distribution || []);
      }
      
      
      // Currently active users endpoint
      const activeUsersResponse = await fetch(`${API_URL}/labor/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (activeUsersResponse.ok) {
        const activeUsersResult = await activeUsersResponse.json();
        setActiveUsers({
          count: safeNumber(activeUsersResult.activeCount),
          list: activeUsersResult.usersList || []
        });
      }
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle date range selection
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setShowDatePicker(range === 'custom');
    
    // When switching to custom, set sensible defaults
    if (range === 'custom') {
      setCustomDateRange({
        startDate: getDateInUserTimezone(-7), // Last 7 days
        endDate: getDateInUserTimezone()
      });
    }
  };
  
  // Safe number conversion utility
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };
  
  // Processed data with sorting and filtering
  const processedBusinessUnitData = useMemo(() => {
    if (!businessUnitData || businessUnitData.length === 0) return [];
    
    const totalHours = businessUnitData.reduce((sum, item) => sum + safeNumber(item.hours), 0);
    
    let processed = businessUnitData
      .filter(item => !hiddenItems.has(item.name))
      .map(item => ({
        ...item,
        hours: safeNumber(item.hours),
        percentage: totalHours > 0 ? (safeNumber(item.hours) / totalHours) * 100 : 0
      }));
    
    // Sort data
    processed.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'percentage':
          aVal = a.percentage;
          bVal = b.percentage;
          break;
        default:
          aVal = a.hours;
          bVal = b.hours;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    // Apply top filter
    if (showOnlyTop && topCount > 0) {
      processed = processed.slice(0, topCount);
    }
    
    return processed;
  }, [businessUnitData, sortBy, sortOrder, showOnlyTop, topCount, hiddenItems]);
  
  const processedActivityData = useMemo(() => {
    if (!activityData || activityData.length === 0) return [];
    
    const totalHours = activityData.reduce((sum, item) => sum + safeNumber(item.hours), 0);
    
    let processed = activityData
      .filter(item => !hiddenItems.has(item.name))
      .map(item => ({
        ...item,
        hours: safeNumber(item.hours),
        percentage: totalHours > 0 ? (safeNumber(item.hours) / totalHours) * 100 : 0
      }));
    
    // Sort by hours descending for better pie chart display
    processed.sort((a, b) => b.hours - a.hours);
    
    // Apply top filter
    if (showOnlyTop && topCount > 0) {
      processed = processed.slice(0, topCount);
    }
    
    return processed;
  }, [activityData, showOnlyTop, topCount, hiddenItems]);
  
  // Custom tooltip for bar charts
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0];
      const value = safeNumber(data.value);
      const totalHours = businessUnitData.reduce((sum, item) => sum + safeNumber(item.hours), 0);
      const percentage = totalHours > 0 ? (value / totalHours) * 100 : 0;
      
      return (
        <div className="bg-slate-800 p-3 border border-slate-700 rounded shadow-lg text-sm min-w-40">
          <p className="text-slate-200 font-semibold mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-blue-400">{`Hours: ${value.toFixed(2)}`}</p>
            <p className="text-green-400">{`Percentage: ${percentage.toFixed(1)}%`}</p>
            <p className="text-purple-400">{`Rank: #${processedBusinessUnitData.findIndex(item => item.name === label) + 1}`}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for pie charts
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const value = safeNumber(data.value);
      const name = data.name;
      const percentage = safeNumber(data.payload.percentage);
      
      return (
        <div className="bg-slate-800 p-3 border border-slate-700 rounded shadow-lg text-sm min-w-40">
          <p className="text-slate-200 font-semibold mb-2">{name}</p>
          <div className="space-y-1">
            <p className="text-blue-400">{`Hours: ${value.toFixed(2)}`}</p>
            <p className="text-green-400">{`Percentage: ${percentage.toFixed(1)}%`}</p>
            <p className="text-purple-400">{`Rank: #${processedActivityData.findIndex(item => item.name === name) + 1}`}</p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  // Handle chart interactions
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };
  
  const toggleItemVisibility = (itemName) => {
    const newHiddenItems = new Set(hiddenItems);
    if (newHiddenItems.has(itemName)) {
      newHiddenItems.delete(itemName);
    } else {
      newHiddenItems.add(itemName);
    }
    setHiddenItems(newHiddenItems);
  };
  
  const handleBarClick = (data) => {
    setSelectedBusinessUnit(selectedBusinessUnit === data.name ? null : data.name);
  };
  
  const handlePieClick = (data) => {
    setSelectedActivity(selectedActivity === data.name ? null : data.name);
  };
  
  return (
    <div className="space-y-6">
      {/* Header with date range selector */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-slate-200">Labor Overview</h2>
          <div className="text-xs text-slate-400 mt-1 space-x-4">
            <span>Timezone: {userTimezone.replace('_', ' ')}</span>
            <span>•</span>
            <span>Period: {getDateRangeDisplayText()}</span>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center px-3 py-2 bg-slate-700 hover:bg-slate-600 
                       text-slate-300 rounded-md transition-colors text-sm"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {dateRange === 'today' ? 'Today' : 
               dateRange === 'yesterday' ? 'Yesterday' : 
               dateRange === 'this_week' ? 'This Week' : 
               dateRange === 'last_week' ? 'Last Week' :
               dateRange === 'this_month' ? 'This Month' : 
               dateRange === 'custom' ? 'Custom Range' : 'Select Period'}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            
            {showDatePicker && (
              <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 
                            rounded-md shadow-lg z-10 py-1">
                <button
                  onClick={() => handleDateRangeChange('today')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  Today
                </button>
                <button
                  onClick={() => handleDateRangeChange('yesterday')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  Yesterday
                </button>
                <button
                  onClick={() => handleDateRangeChange('this_week')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  This Week
                </button>
                <button
                  onClick={() => handleDateRangeChange('last_week')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  Last Week
                </button>
                <button
                  onClick={() => handleDateRangeChange('this_month')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  This Month
                </button>
                <button
                  onClick={() => handleDateRangeChange('custom')}
                  className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  Custom Range
                </button>
              </div>
            )}
          </div>
          
          {dateRange === 'custom' && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange({...customDateRange, startDate: e.target.value})}
                className="px-2 py-1 bg-slate-700 border border-slate-600 rounded-md text-sm text-slate-300"
                max={getDateInUserTimezone()}
              />
              <span className="text-slate-400">to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange({...customDateRange, endDate: e.target.value})}
                className="px-2 py-1 bg-slate-700 border border-slate-600 rounded-md text-sm text-slate-300"
                max={getDateInUserTimezone()}
                min={customDateRange.startDate}
              />
            </div>
          )}
          
          <button
            onClick={fetchDashboardData}
            className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 
                     text-white rounded-md transition-colors text-sm"
            disabled={loading}
            title={`Refresh data (${userTimezone})`}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </button>
        </div>
      </div>
      
      {/* Key Metrics Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Total Hours Card */}
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Total Hours</h3>
            <Clock className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-slate-200">
            {safeNumber(summaryStats.totalHours).toFixed(1)}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Hours logged for the selected period
          </p>
        </div>
        
        {/* Active Employees Card */}
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Active Employees</h3>
            <Users className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-2xl font-bold text-slate-200">{safeNumber(summaryStats.activeEmployees)}</p>
          <p className="text-xs text-slate-400 mt-1">
            Employees who logged time
          </p>
        </div>
        
        {/* Average Hours Card */}
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Avg Hours/Employee</h3>
            <TrendingUp className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-slate-200">
            {safeNumber(summaryStats.avgHoursPerEmployee).toFixed(1)}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Average hours per active employee
          </p>
        </div>
        
        {/* Billable Ratio Card */}
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Billable Ratio</h3>
            <PieChart className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-slate-200">
            {(safeNumber(summaryStats.billableRatio) * 100).toFixed(1)}%
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Percentage of billable hours
          </p>
        </div>
        
        {/* Currently Clocked In Card */}
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Clocked In</h3>
            <Users className="w-5 h-5 text-orange-400" />
          </div>
          <p className="text-2xl font-bold text-slate-200">{safeNumber(activeUsers.count)}</p>
          <p className="text-xs text-slate-400 mt-1">
            {safeNumber(activeUsers.count) > 0 ? 'Currently active' : 'No one clocked in'}
          </p>
        </div>
      </div>
      
      {/* Main Visualizations */}
      <div className="space-y-6">
        {/* Hours by Business Unit */}
        <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-slate-200">Hours by Business Unit</h3>
              <Building2 className="w-6 h-6 text-slate-400" />
              {selectedBusinessUnit && (
                <div className="ml-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                  Selected: {selectedBusinessUnit}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Sort controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleSortChange('hours')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    sortBy === 'hours' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  Hours {sortBy === 'hours' && (sortOrder === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  onClick={() => handleSortChange('name')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    sortBy === 'name' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </button>
                <button
                  onClick={() => handleSortChange('percentage')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    sortBy === 'percentage' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                  }`}
                >
                  % {sortBy === 'percentage' && (sortOrder === 'asc' ? '↑' : '↓')}
                </button>
              </div>
              
              {/* Top filter */}
              <div className="flex items-center gap-1">
                <label className="flex items-center gap-1 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={showOnlyTop}
                    onChange={(e) => setShowOnlyTop(e.target.checked)}
                    className="w-3 h-3"
                  />
                  Top
                </label>
                {showOnlyTop && (
                  <input
                    type="number"
                    value={topCount}
                    onChange={(e) => setTopCount(Math.max(1, parseInt(e.target.value) || 10))}
                    className="w-12 px-1 py-0.5 text-xs bg-slate-600 border border-slate-500 rounded"
                    min="1"
                    max="50"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Data summary */}
          {processedBusinessUnitData.length > 0 && (
            <div className="mb-4 p-3 bg-slate-800/50 rounded border border-slate-600">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="text-slate-300">
                  Showing: <span className="text-blue-400">{processedBusinessUnitData.length}</span> units
                </span>
                <span className="text-slate-300">
                  Total Hours: <span className="text-green-400">
                    {safeNumber(processedBusinessUnitData.reduce((sum, item) => sum + safeNumber(item.hours), 0)).toFixed(1)}
                  </span>
                </span>
                <span className="text-slate-300">
                  Avg per Unit: <span className="text-yellow-400">
                    {processedBusinessUnitData.length > 0 ? (safeNumber(processedBusinessUnitData.reduce((sum, item) => sum + safeNumber(item.hours), 0)) / processedBusinessUnitData.length).toFixed(1) : '0.0'}
                  </span>
                </span>
                {hiddenItems.size > 0 && (
                  <span className="text-slate-300">
                    Hidden: <span className="text-red-400">{hiddenItems.size}</span>
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="h-96">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : processedBusinessUnitData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processedBusinessUnitData}
                  margin={{ top: 20, right: 30, left: 40, bottom: 80 }}
                  barCategoryGap="20%"
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80} 
                    interval={0}
                    tick={{ fill: '#CBD5E1', fontSize: 11 }} 
                  />
                  <YAxis 
                    tick={{ fill: '#CBD5E1', fontSize: 10 }} 
                    label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#CBD5E1', fontSize: 12 }}
                    domain={[0, 'dataMax + 2']}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar 
                    dataKey="hours" 
                    fill={(entry, index) => {
                      const isSelected = selectedBusinessUnit === entry?.name;
                      const colors = generateColors(processedBusinessUnitData.length);
                      return isSelected ? '#F59E0B' : colors[index % colors.length];
                    }}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                    cursor="pointer"
                    onClick={handleBarClick}
                  >
                    {processedBusinessUnitData.map((entry, index) => {
                      const isSelected = selectedBusinessUnit === entry.name;
                      const colors = generateColors(processedBusinessUnitData.length);
                      return (
                        <Cell 
                          key={`cell-${index}`}
                          fill={isSelected ? '#F59E0B' : colors[index % colors.length]}
                          stroke={isSelected ? '#D97706' : 'transparent'}
                          strokeWidth={isSelected ? 2 : 0}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-slate-400">
                  <BarChart2 className="w-16 h-16 mx-auto text-slate-600" />
                  <p className="mt-2">No data available for this period</p>
                  {hiddenItems.size > 0 && (
                    <p className="mt-1 text-sm">({hiddenItems.size} items hidden)</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Activity Distribution */}
        <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-slate-200">Activity Distribution</h3>
              <Activity className="w-6 h-6 text-slate-400" />
              {selectedActivity && (
                <div className="ml-2 px-2 py-1 bg-purple-600 text-white text-xs rounded">
                  Selected: {selectedActivity}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              {/* Top filter */}
              <div className="flex items-center gap-1">
                <label className="flex items-center gap-1 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={showOnlyTop}
                    onChange={(e) => setShowOnlyTop(e.target.checked)}
                    className="w-3 h-3"
                  />
                  Top
                </label>
                {showOnlyTop && (
                  <input
                    type="number"
                    value={topCount}
                    onChange={(e) => setTopCount(Math.max(1, parseInt(e.target.value) || 10))}
                    className="w-12 px-1 py-0.5 text-xs bg-slate-600 border border-slate-500 rounded"
                    min="1"
                    max="20"
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Data summary */}
          {processedActivityData.length > 0 && (
            <div className="mb-4 p-3 bg-slate-800/50 rounded border border-slate-600">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="text-slate-300">
                  Showing: <span className="text-blue-400">{processedActivityData.length}</span> activities
                </span>
                <span className="text-slate-300">
                  Total Hours: <span className="text-green-400">
                    {safeNumber(processedActivityData.reduce((sum, item) => sum + safeNumber(item.hours), 0)).toFixed(1)}
                  </span>
                </span>
                <span className="text-slate-300">
                  Top Activity: <span className="text-purple-400">
                    {processedActivityData[0]?.name || 'N/A'} ({safeNumber(processedActivityData[0]?.percentage).toFixed(1)}%)
                  </span>
                </span>
                {hiddenItems.size > 0 && (
                  <span className="text-slate-300">
                    Hidden: <span className="text-red-400">{hiddenItems.size}</span>
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Pie Chart */}
            <div className="flex-1 h-96">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : processedActivityData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={processedActivityData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.percentage > 5 ? `${entry.percentage.toFixed(1)}%` : ''}
                      outerRadius={120}
                      innerRadius={50}
                      fill="#8884d8"
                      dataKey="hours"
                      stroke="#1e293b"
                      strokeWidth={2}
                      onClick={handlePieClick}
                      cursor="pointer"
                    >
                      {processedActivityData.map((entry, index) => {
                        const isSelected = selectedActivity === entry.name;
                        const colors = generateColors(processedActivityData.length);
                        return (
                          <Cell 
                            key={`cell-${index}`}
                            fill={isSelected ? '#F59E0B' : colors[index % colors.length]}
                            stroke={isSelected ? '#D97706' : '#1e293b'}
                            strokeWidth={isSelected ? 3 : 2}
                          />
                        );
                      })}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <PieChart className="w-16 h-16 mx-auto text-slate-600" />
                    <p className="mt-2">No data available for this period</p>
                    {hiddenItems.size > 0 && (
                      <p className="mt-1 text-sm">({hiddenItems.size} items hidden)</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Activity List */}
            {processedActivityData.length > 0 && (
              <div className="w-full lg:w-80 bg-slate-800/50 rounded border border-slate-600 p-4">
                <h4 className="text-sm font-medium text-slate-200 mb-3">Activity Breakdown</h4>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {processedActivityData.map((activity, index) => {
                    const colors = generateColors(processedActivityData.length);
                    const isSelected = selectedActivity === activity.name;
                    const isHidden = hiddenItems.has(activity.name);
                    
                    return (
                      <div 
                        key={activity.name}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                          isSelected ? 'bg-purple-600/20 border border-purple-500' : 'hover:bg-slate-700/50'
                        } ${isHidden ? 'opacity-50' : ''}`}
                        onClick={() => handlePieClick(activity)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: colors[index % colors.length] }}
                          ></div>
                          <span className="text-sm text-slate-300 truncate" title={activity.name}>
                            {activity.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-400">
                            {safeNumber(activity.hours).toFixed(1)}h
                          </span>
                          <span className="text-slate-500">
                            {safeNumber(activity.percentage).toFixed(1)}%
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleItemVisibility(activity.name);
                            }}
                            className="text-slate-500 hover:text-slate-300 transition-colors"
                            title={isHidden ? 'Show' : 'Hide'}
                          >
                            {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default OverviewTab;
